import { IconName } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { S_WebButtonSmaller } from 'elements/button/WebButton';
import { ImageMask } from 'elements/visual/ImageMask';
import { EColors, EFonts, EGrads, EShadows } from 'libs/styles/styling';
import styled, { css } from 'styled-components/macro';

interface Props {
    iconName: IconName;
    title: string;
    price: string;
    priceSmall?: string;
    description: string;
    points?: {
        valid: boolean;
        text: string;
    }[];
    pointsText?: JSX.Element;
    buttonText: string;
    buttonType?: 'primary' | 'secondary';
    buttonDisabled?: boolean;
    buttonLoading?: boolean;
    selected?: boolean;
    buttonAction: () => void;
    className?: string;
}

export const UpgradeOffer = (props: Props): JSX.Element => {
    return (
        <S_UpgradeOffer className={props.className}>
            <S_Top>
                <S_Head>
                    {props.selected && (
                        <S_Selected>
                            <S_SelectedIcon icon={['far', 'check']} />
                        </S_Selected>
                    )}
                    <S_Circle>
                        <S_Icon icon={['fal', props.iconName]} />
                        <S_Title>{props.title}</S_Title>
                        <ImageMask
                            randomizePosition={true}
                            step={20}
                            image="contour"
                            repeat={true}
                        />
                    </S_Circle>
                    <S_Price>
                        <S_PriceBig>{props.price}</S_PriceBig>
                        {props.priceSmall && <S_PriceSmall>{props.priceSmall}</S_PriceSmall>}
                    </S_Price>
                    <ImageMask
                        randomizePosition={true}
                        step={20}
                        image="city"
                        repeat={true}
                        initOpacity={1}
                        finalOpacity={0}
                        degress={180}
                    />
                </S_Head>
                <S_Description>
                    <S_Desc>{props.description}</S_Desc>
                </S_Description>
                {props.points && (
                    <S_Points>
                        {props.points?.map((point) => (
                            <S_Point key={point.text}>
                                <S_PointIcon
                                    icon={['fas', point.valid ? 'circle-check' : 'circle-xmark']}
                                    $valid={point.valid}
                                />
                                <S_PointText>{point.text}</S_PointText>
                            </S_Point>
                        ))}
                    </S_Points>
                )}
                {props.pointsText && <S_PointsText>{props.pointsText}</S_PointsText>}
            </S_Top>
            <S_End>
                <S_WebButtonSmaller
                    text={props.buttonText}
                    type={props.buttonType ?? 'primary'}
                    disabled={props.buttonDisabled}
                    loading={props.buttonLoading}
                    shadow={EShadows.LIGHT}
                    onClick={() => {
                        props.buttonAction();
                    }}
                />
            </S_End>
        </S_UpgradeOffer>
    );
};

const S_UpgradeOffer = styled.div`
    width: 250px;
    border-radius: 7px;
    overflow: hidden;
    background-color: #efefef;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
`;

const S_Top = styled.div`
    display: flex;
    flex-direction: column;
`;

const S_Head = styled.div`
    height: 200px;
    display: flex;
    flex-direction: column;
    position: relative;
    justify-content: center;
    align-items: center;
    overflow: visible;
`;

const S_Selected = styled.div`
    width: 40px;
    height: 40px;
    border-radius: 50%;
    position: absolute;
    top: 10px;
    right: 10px;
    background-color: ${EColors.DARK};
    z-index: 100;
    display: flex;
    justify-content: center;
    align-items: center;
`;

const S_SelectedIcon = styled(FontAwesomeIcon)`
    color: white;
    font-size: 20px;
`;

const S_Circle = styled.div`
    width: 100px;
    height: 100px;
    border-radius: 50%;
    background: ${EGrads.ACC2_GRAD};
    display: flex;
    justify-content: center;
    flex-direction: column;
    position: relative;
    z-index: 1;
    align-items: center;
    margin: 10px 0 20px 0;
`;

const S_Icon = styled(FontAwesomeIcon)`
    color: white;
    font-size: 20px;
    margin: 0 0 12px 0;
`;

const S_Title = styled.div`
    color: white;
    font-size: 15px;
    font-family: ${EFonts.Medium};
`;

const S_Price = styled.div`
    display: flex;
    position: relative;
    z-index: 1;
    align-items: flex-end;
`;

const S_PriceBig = styled.div`
    color: ${EColors.DARK};
    font-size: 24px;
    font-family: ${EFonts.Medium};
`;

const S_PriceSmall = styled.div`
    color: ${EColors.DARK};
    font-size: 15px;
    font-family: ${EFonts.Medium};
    margin: 0 0 0 5px;
`;

const S_Description = styled.div`
    height: 45px;
    background-color: #eaeaea;
    display: flex;
    justify-content: center;
    align-items: center;
`;

const S_Desc = styled.div`
    color: ${EColors.DARK};
    font-size: 15px;
    font-family: ${EFonts.Medium};
`;

const S_Points = styled.div`
    display: flex;
    flex-direction: column;
`;

const S_Point = styled.div`
    display: flex;
    border-bottom: 1px solid #dadada;
    align-items: center;
`;

const S_PointIcon = styled(FontAwesomeIcon)<{ $valid: boolean }>`
    font-size: 18px;
    margin: 12px 15px 12px 15px;
    ${({ $valid }) => {
        if ($valid) {
            return css`
                path {
                    fill: url(#acc2Gradient);
                }
            `;
        } else {
            return css`
                color: #d0d0d0;
            `;
        }
    }}
`;

const S_PointText = styled.div`
    color: ${EColors.DARK};
    font-size: 14px;
    font-family: ${EFonts.Regular};
`;

const S_End = styled.div`
    display: flex;
    justify-content: center;
    margin: 20px 0 20px 0;
`;

const S_PointsText = styled.div`
    color: ${EColors.DARK};
    font-size: 13px;
    font-family: ${EFonts.Regular};
    line-height: 20px;
    padding: 20px 20px 0 20px;
`;
